'use client';

import React, { useRef } from 'react';
import Link from 'next/link';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import NavDropdown from '../NavDropdown/NavDropdown';
import { NavOption } from '../../types';

const NOOP = () => {
  /* no-op */
};

interface PrimaryNavItemProps {
  label: string;
  path: string | null;
  icon: IconDefinition;
  isActive: boolean;
  hasDropdown?: boolean;
  onClick?: () => void;
  dropdownOpen?: boolean;
  dropdownOptions?: NavOption[];
  onDropdownClose?: () => void;
}

const PrimaryNavItem: React.FC<PrimaryNavItemProps> = ({
  label,
  path,
  icon,
  isActive,
  hasDropdown = false,
  onClick,
  dropdownOpen,
  dropdownOptions = [],
  onDropdownClose,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Dropdown nav item (Reports, Analytics, etc.)
  if (hasDropdown) {
    return (
      <>
        <div
          ref={containerRef}
          className="flex flex-col justify-start items-center gap-0.5 cursor-pointer relative"
          onClick={onClick}
          role="button"
          tabIndex={0}
          aria-haspopup="true"
          aria-expanded={dropdownOpen}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              onClick?.();
            }
          }}
        >
          <div
            className={`w-10 h-10 flex items-center justify-center rounded-xl hover:bg-sky-700 ${
              isActive ? 'bg-sky-700' : ''
            }`}
          >
            <Icon icon={icon} className="w-5 h-5 text-white" />
          </div>
          <div className="text-center justify-center text-white text-[9px] font-semibold font-['Inter'] leading-3">
            {label}
          </div>
        </div>
        <NavDropdown
          containerRef={containerRef}
          dropdownRef={dropdownRef}
          open={dropdownOpen || false}
          onClose={onDropdownClose || NOOP}
          options={dropdownOptions}
          testId={`sidebar-${label.toLowerCase()}-menu`}
        />
      </>
    );
  }

  // Regular nav item with link
  return (
    <Link
      href={path || '/'}
      className="flex flex-col justify-start items-center gap-0.5"
    >
      <div
        className={`w-10 h-10 flex items-center justify-center rounded-xl hover:bg-sky-700 ${
          isActive ? 'bg-sky-700' : ''
        }`}
      >
        <div className="w-6 h-6 flex items-center justify-center">
          <Icon icon={icon} size="lg" className="text-white" />
        </div>
      </div>
      <div className="text-center justify-center text-white text-[9px] font-semibold font-['Inter'] leading-3">
        {label}
      </div>
    </Link>
  );
};

export default React.memo(PrimaryNavItem);
